import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Tooltip,
  Paper,
  CircularProgress,
  Button,
  Divider,
  Popover,
  Snackbar,
  Alert,
  Chip,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Description as DocIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  AudioFile as AudioIcon,
  VideoFile as VideoIcon,
  InsertDriveFile as FileIcon,
  Print as PrintIcon,
  OpenInNew as OpenInNewIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  SaveAlt as SaveAltIcon,
  Forward as ForwardIcon,
  ContentCopy as CopyIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Label as TagIcon,
  Share as ShareIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import api, { extractData } from '../../utils/api';

export interface Attachment {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  url?: string;
  contentId?: string;
}

interface AttachmentPreviewProps {
  attachments: Attachment[];
  initialIndex?: number;
  open: boolean;
  onClose: () => void;
  onDownload?: (attachment: Attachment) => void;
  onForward?: (attachment: Attachment) => void;
  emailId?: string;
}

// Get icon based on MIME type
const getFileIcon = (mimeType: string) => {
  if (mimeType.startsWith('image/')) return <ImageIcon sx={{ fontSize: 48 }} />;
  if (mimeType === 'application/pdf') return <PdfIcon sx={{ fontSize: 48, color: '#ea4335' }} />;
  if (mimeType.startsWith('audio/')) return <AudioIcon sx={{ fontSize: 48, color: '#4285f4' }} />;
  if (mimeType.startsWith('video/')) return <VideoIcon sx={{ fontSize: 48, color: '#9c27b0' }} />;
  if (mimeType.includes('document') || mimeType.includes('word')) return <DocIcon sx={{ fontSize: 48, color: '#4285f4' }} />;
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return <DocIcon sx={{ fontSize: 48, color: '#34a853' }} />;
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return <DocIcon sx={{ fontSize: 48, color: '#fbbc04' }} />;
  return <FileIcon sx={{ fontSize: 48, color: '#5f6368' }} />;
};

// Check if MIME type supports preview
const canPreview = (mimeType: string): boolean => {
  return (
    mimeType.startsWith('image/') ||
    mimeType === 'application/pdf' ||
    mimeType.startsWith('audio/') ||
    mimeType.startsWith('video/') ||
    mimeType === 'text/plain' ||
    isOfficeDocument(mimeType)
  );
};

// Check if MIME type is an office document viewable by OnlyOffice
const isOfficeDocument = (mimeType: string): boolean => {
  const officeTypes = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.oasis.opendocument.presentation',
    'text/csv',
    'application/rtf',
  ];
  return officeTypes.includes(mimeType);
};

// Get OnlyOffice document type from MIME type
const getOnlyOfficeDocType = (mimeType: string): 'word' | 'cell' | 'slide' => {
  if (
    mimeType.includes('spreadsheet') ||
    mimeType.includes('excel') ||
    mimeType === 'text/csv'
  ) return 'cell';
  if (
    mimeType.includes('presentation') ||
    mimeType.includes('powerpoint')
  ) return 'slide';
  return 'word';
};

// Get file extension from filename
const getFileExtension = (filename: string): string => {
  return filename.split('.').pop()?.toLowerCase() || '';
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({
  attachments,
  initialIndex = 0,
  open,
  onClose,
  onDownload,
  onForward,
  emailId,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [editorConfig, setEditorConfig] = useState<{ config: any; onlyofficeUrl: string } | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [infoAnchor, setInfoAnchor] = useState<HTMLElement | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'info' });
  const [savingToDocs, setSavingToDocs] = useState(false);
  const [tagAnchor, setTagAnchor] = useState<HTMLElement | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [attachmentTags, setAttachmentTags] = useState<string[]>([]);
  const [shareAnchor, setShareAnchor] = useState<HTMLElement | null>(null);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  const currentAttachment = attachments[currentIndex];
  // Stable identity for the current attachment to avoid re-fetching on poll-triggered re-renders
  const currentAttachmentKey = currentAttachment ? `${currentIndex}-${currentAttachment.filename}-${currentAttachment.size}` : '';

  // Fetch blob URL for non-office attachments (images, PDFs, audio, video)
  const fetchBlobUrl = useCallback(async (att: Attachment) => {
    if (!emailId || isOfficeDocument(att.mimeType)) return;
    // Only fetch for types we can render in the browser
    const needsBlob = att.mimeType.startsWith('image/') ||
      att.mimeType === 'application/pdf' ||
      att.mimeType.startsWith('audio/') ||
      att.mimeType.startsWith('video/') ||
      att.mimeType === 'text/plain';
    if (!needsBlob) return;
    if (att.url) { setBlobUrl(att.url); return; }

    setIsLoading(true);
    try {
      const response = await api.get(
        `/emails/${emailId}/attachments/${encodeURIComponent(att.filename)}`,
        { responseType: 'blob' },
      );
      const blob = new Blob([response.data], { type: att.mimeType });
      const url = window.URL.createObjectURL(blob);
      setBlobUrl(url);
    } catch {
      setError('Failed to load attachment');
    } finally {
      setIsLoading(false);
    }
  }, [emailId]);

  useEffect(() => {
    // Cleanup previous blob URL
    return () => {
      if (blobUrl && blobUrl.startsWith('blob:')) {
        window.URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  // Fetch full signed OnlyOffice editor config for office documents
  const lastFetchedKeyRef = useRef<string>('');
  const fetchPreviewToken = useCallback(async (att: Attachment, attachmentKey: string) => {
    if (!emailId || !isOfficeDocument(att.mimeType)) {
      setPreviewUrl(null);
      setEditorConfig(null);
      return;
    }
    // Skip re-fetch if we already have a config for the same attachment
    if (lastFetchedKeyRef.current === attachmentKey) return;
    lastFetchedKeyRef.current = attachmentKey;
    setPreviewLoading(true);
    setPreviewUrl(null);
    setError(null); // Clear previous errors
    // Don't clear editorConfig here — keep showing old doc while new one loads
    try {
      const res = await api.get(`/emails/${emailId}/attachments/${encodeURIComponent(att.filename)}/editor-config`);
      const data = extractData<{ config: any; onlyofficeUrl: string }>(res);
      if (data.config && data.onlyofficeUrl) {
        setEditorConfig(data);
        setPreviewUrl('ready'); // Signal that we have a config
      } else {
        setError('Invalid document configuration');
        setPreviewUrl(null);
        setEditorConfig(null);
      }
    } catch (err) {
      console.error('Failed to get editor config:', err);
      setError('Document preview unavailable');
      setPreviewUrl(null);
      setEditorConfig(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [emailId]);

  useEffect(() => {
    if (open && currentAttachment) {
      setBlobUrl(null);
      setError(null);
      if (isOfficeDocument(currentAttachment.mimeType)) {
        fetchPreviewToken(currentAttachment, currentAttachmentKey);
      } else {
        setPreviewUrl(null);
        fetchBlobUrl(currentAttachment);
      }
    } else {
      setPreviewUrl(null);
      setBlobUrl(null);
    }
    // Use stable key instead of object reference to prevent re-fetch on polling
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentAttachmentKey]);

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setZoom(1);
      setError(null);
    }
  };

  const handleNext = () => {
    if (currentIndex < attachments.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setZoom(1);
      setError(null);
    }
  };

  const handleZoomIn = () => {
    setZoom(Math.min(zoom + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(Math.max(zoom - 0.25, 0.25));
  };

  const handleDownload = () => {
    if (onDownload && currentAttachment) {
      onDownload(currentAttachment);
    } else if (blobUrl || currentAttachment?.url) {
      const link = document.createElement('a');
      link.href = blobUrl || currentAttachment!.url || '';
      link.download = currentAttachment!.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePrint = () => {
    if (!currentAttachment) return;
    const { mimeType } = currentAttachment;
    const url = blobUrl || currentAttachment.url;

    if (mimeType.startsWith('image/') && url) {
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(`<html><head><title>Print</title></head><body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh"><img src="${url}" style="max-width:100%;max-height:100vh" onload="window.print();window.close()"/></body></html>`);
        w.document.close();
      }
    } else if (mimeType === 'application/pdf' && url) {
      const w = window.open(url, '_blank');
      if (w) { w.addEventListener('load', () => { w.print(); }); }
    } else if (isOfficeDocument(mimeType)) {
      // For office docs, trigger print via the OnlyOffice editor iframe
      const iframe = dialogRef.current?.querySelector('iframe');
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage({ type: 'oo-print' }, '*');
      }
      setSnackbar({ open: true, message: 'Use Ctrl+P in the document editor to print', severity: 'info' });
    }
  };

  const handleOpenInNewTab = () => {
    if (!currentAttachment || !emailId) return;
    if (isOfficeDocument(currentAttachment.mimeType) && editorConfig) {
      // Open the OnlyOffice editor in a new tab
      const url = `/attachment-editor?emailId=${emailId}&filename=${encodeURIComponent(currentAttachment.filename)}`;
      window.open(url, '_blank');
    } else if (blobUrl) {
      window.open(blobUrl, '_blank');
    }
  };

  const handleToggleFullscreen = () => {
    const el = dialogRef.current?.closest('.MuiDialog-root') as HTMLElement;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen?.().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const handleSaveToDocs = async () => {
    if (!currentAttachment || !emailId) return;
    setSavingToDocs(true);
    try {
      await api.post('/documents/save-from-attachment', {
        emailId,
        filename: currentAttachment.filename,
      });
      setSnackbar({ open: true, message: `"${currentAttachment.filename}" saved to Cruvz Documents`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to save to Documents', severity: 'error' });
    } finally {
      setSavingToDocs(false);
    }
  };

  const handleForward = () => {
    if (onForward && currentAttachment) {
      onForward(currentAttachment);
    } else {
      setSnackbar({ open: true, message: 'Forward feature — compose window will open', severity: 'info' });
    }
  };

  const handleCopyImage = async () => {
    if (!currentAttachment?.mimeType.startsWith('image/') || !blobUrl) return;
    try {
      const response = await fetch(blobUrl);
      const blob = await response.blob();
      await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
      setSnackbar({ open: true, message: 'Image copied to clipboard', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to copy image', severity: 'error' });
    }
  };

  // Tag handlers
  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !attachmentTags.includes(tag)) {
      const newTags = [...attachmentTags, tag];
      setAttachmentTags(newTags);
      setTagInput('');
      // Persist to backend
      if (emailId && currentAttachment) {
        api.post(`/emails/${emailId}/attachments/${encodeURIComponent(currentAttachment.filename)}/tags`, { tags: newTags }).catch(() => {});
      }
    }
  };

  const handleRemoveTag = (tag: string) => {
    const newTags = attachmentTags.filter(t => t !== tag);
    setAttachmentTags(newTags);
    if (emailId && currentAttachment) {
      api.post(`/emails/${emailId}/attachments/${encodeURIComponent(currentAttachment.filename)}/tags`, { tags: newTags }).catch(() => {});
    }
  };

  // Fetch existing tags when attachment changes (optional feature - silently fail if not implemented)
  useEffect(() => {
    if (open && emailId && currentAttachment) {
      api.get(`/emails/${emailId}/attachments/${encodeURIComponent(currentAttachment.filename)}/tags`)
        .then(res => {
          const data = extractData<{ tags: string[] }>(res);
          setAttachmentTags(data.tags || []);
        })
        .catch((err) => {
          // Silently ignore 404 - tags feature may not be implemented yet
          if (err?.response?.status !== 404) {
            console.warn('[AttachmentPreview] Failed to load tags:', err);
          }
          setAttachmentTags([]);
        });
    }
  }, [open, currentAttachmentKey, emailId]);

  // Share handlers
  const handleGenerateShareLink = async () => {
    if (!emailId || !currentAttachment) return;
    setGeneratingLink(true);
    try {
      const res = await api.post(`/emails/${emailId}/attachments/${encodeURIComponent(currentAttachment.filename)}/share`);
      const data = extractData<{ shareUrl: string }>(res);
      setShareLink(data.shareUrl);
    } catch {
      setSnackbar({ open: true, message: 'Failed to generate share link', severity: 'error' });
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyShareLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setSnackbar({ open: true, message: 'Share link copied to clipboard', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to copy link', severity: 'error' });
    }
  };

  const handleShareViaEmail = () => {
    if (!shareLink || !currentAttachment) return;
    setShareAnchor(null);
    // Open compose with the share link in body
    if (onForward) {
      onForward(currentAttachment);
    } else {
      const subject = encodeURIComponent(`Shared: ${currentAttachment.filename}`);
      const body = encodeURIComponent(`Here is a shared attachment:\n\n${shareLink}`);
      window.open(`mailto:?subject=${subject}&body=${body}`, '_self');
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') handlePrev();
      else if (e.key === 'ArrowRight') handleNext();
      else if (e.key === 'Escape') onClose();
      else if (e.key === '+' || e.key === '=') handleZoomIn();
      else if (e.key === '-') handleZoomOut();
      else if (e.ctrlKey && e.key === 'p') { e.preventDefault(); handlePrint(); }
      else if (e.key === 'f' && !e.ctrlKey) handleToggleFullscreen();
      else if (e.key === 'd' && !e.ctrlKey) setIsDarkMode(prev => !prev);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentIndex, zoom, blobUrl]);

  const renderPreview = () => {
    if (!currentAttachment) return null;

    const { mimeType, filename } = currentAttachment;
    const effectiveUrl = blobUrl || currentAttachment.url;

    if (!effectiveUrl && !canPreview(mimeType)) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            p: 4,
          }}
        >
          {getFileIcon(mimeType)}
          <Typography variant="h6" sx={{ mt: 2 }}>
            {filename}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {formatFileSize(currentAttachment.size)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Preview not available for this file type
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            p: 4,
            gap: 2,
          }}
        >
          {getFileIcon(mimeType)}
          <Typography variant="body1" color="error" sx={{ mt: 2 }}>
            Failed to load preview
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ mt: 2 }}
          >
            Download {getFileExtension(filename).toUpperCase()} File
          </Button>
        </Box>
      );
    }

    // Image preview
    if (mimeType.startsWith('image/')) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            overflow: 'auto',
            bgcolor: '#000',
          }}
        >
          {isLoading && (
            <CircularProgress sx={{ position: 'absolute' }} />
          )}
          <img
            src={effectiveUrl}
            alt={filename}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              transform: `scale(${zoom})`,
              transition: 'transform 0.2s ease',
            }}
            onLoad={() => setIsLoading(false)}
            onError={() => {
              setIsLoading(false);
              setError('Failed to load image');
            }}
          />
        </Box>
      );
    }

    // PDF preview
    if (mimeType === 'application/pdf') {
      return (
        <Box sx={{ height: '100%', width: '100%' }}>
          <iframe
            src={`${effectiveUrl}#toolbar=0`}
            title={filename}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
          />
        </Box>
      );
    }

    // Audio preview
    if (mimeType.startsWith('audio/')) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            p: 4,
          }}
        >
          {getFileIcon(mimeType)}
          <Typography variant="h6" sx={{ mt: 2, mb: 3 }}>
            {filename}
          </Typography>
          <audio controls src={effectiveUrl} style={{ width: '100%', maxWidth: 400 }}>
            Your browser does not support the audio element.
          </audio>
        </Box>
      );
    }

    // Video preview
    if (mimeType.startsWith('video/')) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            bgcolor: '#000',
          }}
        >
          <video
            controls
            src={effectiveUrl}
            style={{ maxWidth: '100%', maxHeight: '100%' }}
          >
            Your browser does not support the video element.
          </video>
        </Box>
      );
    }

    // Text preview
    if (mimeType === 'text/plain') {
      return (
        <Box
          sx={{
            height: '100%',
            overflow: 'auto',
            p: 3,
            bgcolor: '#f5f5f5',
          }}
        >
          <Paper sx={{ p: 2 }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {/* Text content would be fetched here */}
              Loading text content...
            </pre>
          </Paper>
        </Box>
      );
    }

    // Office document preview via OnlyOffice
    if (isOfficeDocument(mimeType) && emailId) {
      if (previewLoading) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Preparing document preview...</Typography>
          </Box>
        );
      }
      if (!editorConfig || error) {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4, gap: 2 }}>
            {getFileIcon(mimeType)}
            <Typography variant="h6" sx={{ mt: 2 }}>{filename}</Typography>
            <Typography variant="body2" color="text.secondary">
              {formatFileSize(currentAttachment.size)}
            </Typography>
            {error && (
              <Typography color="error" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
            <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
              Document preview requires OnlyOffice Document Server.<br />
              Click the download button below to view this file.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{ mt: 2 }}
            >
              Download {getFileExtension(filename).toUpperCase()} File
            </Button>
          </Box>
        );
      }

      return (
        <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
          {isLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', position: 'absolute', inset: 0, zIndex: 1, bgcolor: 'rgba(255,255,255,0.8)' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading document preview...</Typography>
            </Box>
          )}
          <OnlyOfficeViewer
            config={editorConfig.config}
            onlyofficeUrl={editorConfig.onlyofficeUrl}
            onReady={() => setIsLoading(false)}
            onError={() => { setIsLoading(false); setError('Failed to load document preview. Document server may be unavailable.'); }}
          />
        </Box>
      );
    }

    // No preview available
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 4,
        }}
      >
        {getFileIcon(mimeType)}
        <Typography variant="h6" sx={{ mt: 2 }}>
          {filename}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {formatFileSize(currentAttachment.size)}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Preview not available for this file type
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        ref: dialogRef,
        sx: {
          height: '90vh',
          maxHeight: '95vh',
          width: '95vw',
          maxWidth: '95vw',
          bgcolor: isDarkMode ? '#1a1a2e' : 'background.paper',
        },
      }}
    >
      {/* Enhanced Header Toolbar */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'divider',
          py: 0.75,
          px: 2,
          bgcolor: isDarkMode ? '#16213e' : '#fafafa',
          minHeight: 48,
        }}
      >
        {/* Left: Filename + counter */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: '0 1 auto' }}>
          <Box sx={{ color: isDarkMode ? '#90caf9' : 'text.secondary', display: 'flex' }}>
            {currentAttachment && getFileIcon(currentAttachment.mimeType)}
          </Box>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            noWrap
            sx={{ maxWidth: 280, color: isDarkMode ? '#fff' : 'text.primary' }}
          >
            {currentAttachment?.filename}
          </Typography>
          {attachments.length > 1 && (
            <Typography variant="caption" sx={{ color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'text.secondary', whiteSpace: 'nowrap' }}>
              {currentIndex + 1} / {attachments.length}
            </Typography>
          )}
        </Box>

        {/* Center: Action buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          {/* Zoom controls for images */}
          {currentAttachment?.mimeType.startsWith('image/') && (
            <>
              <Tooltip title="Zoom out (−)">
                <IconButton size="small" onClick={handleZoomOut} disabled={zoom <= 0.25} sx={{ color: isDarkMode ? '#90caf9' : undefined }}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'center', color: isDarkMode ? '#fff' : undefined }}>
                {Math.round(zoom * 100)}%
              </Typography>
              <Tooltip title="Zoom in (+)">
                <IconButton size="small" onClick={handleZoomIn} disabled={zoom >= 3} sx={{ color: isDarkMode ? '#90caf9' : undefined }}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: isDarkMode ? 'rgba(255,255,255,0.15)' : undefined }} />
            </>
          )}

          {/* Copy to clipboard (images only) */}
          {currentAttachment?.mimeType.startsWith('image/') && blobUrl && (
            <Tooltip title="Copy image to clipboard">
              <IconButton size="small" onClick={handleCopyImage} sx={{ color: isDarkMode ? '#90caf9' : undefined }}>
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* Print */}
          <Tooltip title="Print (Ctrl+P)">
            <IconButton size="small" onClick={handlePrint} sx={{ color: isDarkMode ? '#90caf9' : undefined }}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Download */}
          <Tooltip title="Download">
            <IconButton size="small" onClick={handleDownload} sx={{ color: isDarkMode ? '#90caf9' : undefined }}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Save to Cruvz Documents */}
          <Tooltip title="Save to Cruvz Documents">
            <span>
              <IconButton size="small" onClick={handleSaveToDocs} disabled={savingToDocs || !emailId} sx={{ color: isDarkMode ? '#90caf9' : undefined }}>
                {savingToDocs ? <CircularProgress size={18} /> : <SaveAltIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>

          {/* Forward */}
          <Tooltip title="Forward attachment via email">
            <IconButton size="small" onClick={handleForward} sx={{ color: isDarkMode ? '#90caf9' : undefined }}>
              <ForwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Tag */}
          <Tooltip title="Tag attachment">
            <IconButton size="small" onClick={(e) => setTagAnchor(e.currentTarget)} sx={{ color: isDarkMode ? '#90caf9' : undefined }}>
              <TagIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Share */}
          <Tooltip title="Share attachment">
            <IconButton size="small" onClick={(e) => { setShareAnchor(e.currentTarget); if (!shareLink) handleGenerateShareLink(); }} sx={{ color: isDarkMode ? '#90caf9' : undefined }}>
              <ShareIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: isDarkMode ? 'rgba(255,255,255,0.15)' : undefined }} />

          {/* Open in new tab */}
          <Tooltip title="Open in new tab">
            <IconButton size="small" onClick={handleOpenInNewTab} sx={{ color: isDarkMode ? '#90caf9' : undefined }}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Fullscreen */}
          <Tooltip title={isFullscreen ? 'Exit fullscreen (F)' : 'Fullscreen (F)'}>
            <IconButton size="small" onClick={handleToggleFullscreen} sx={{ color: isDarkMode ? '#90caf9' : undefined }}>
              {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          {/* Dark mode */}
          <Tooltip title={isDarkMode ? 'Light mode (D)' : 'Dark mode (D)'}>
            <IconButton size="small" onClick={() => setIsDarkMode(!isDarkMode)} sx={{ color: isDarkMode ? '#ffd54f' : undefined }}>
              {isDarkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          {/* File info */}
          <Tooltip title="File info">
            <IconButton size="small" onClick={(e) => setInfoAnchor(e.currentTarget)} sx={{ color: isDarkMode ? '#90caf9' : undefined }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: isDarkMode ? 'rgba(255,255,255,0.15)' : undefined }} />

          {/* Edit in Cruvz Workspace - prominent button for office docs */}
          {currentAttachment && isOfficeDocument(currentAttachment.mimeType) && (
            <Button
              variant="contained"
              size="small"
              startIcon={<EditIcon />}
              onClick={handleOpenInNewTab}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 1.5,
                py: 0.5,
                fontSize: '0.75rem',
                borderRadius: 1,
                bgcolor: '#1a73e8',
                '&:hover': { bgcolor: '#1557b0' },
                mr: 0.5,
              }}
            >
              Edit in Cruvz Workspace
            </Button>
          )}

          {/* Close */}
          <Tooltip title="Close (Esc)">
            <IconButton size="small" onClick={onClose} sx={{ color: isDarkMode ? '#ef9a9a' : undefined }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      {/* File Info Popover */}
      <Popover
        open={Boolean(infoAnchor)}
        anchorEl={infoAnchor}
        onClose={() => setInfoAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {currentAttachment && (
          <Box sx={{ p: 2, minWidth: 240 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>File Information</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: '0.85rem' }}>
              <Typography variant="caption" color="text.secondary">Name</Typography>
              <Typography variant="caption">{currentAttachment.filename}</Typography>
              <Typography variant="caption" color="text.secondary">Size</Typography>
              <Typography variant="caption">{formatFileSize(currentAttachment.size)}</Typography>
              <Typography variant="caption" color="text.secondary">Type</Typography>
              <Typography variant="caption">{currentAttachment.mimeType}</Typography>
              <Typography variant="caption" color="text.secondary">Extension</Typography>
              <Typography variant="caption">.{getFileExtension(currentAttachment.filename)}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Keyboard: ← → Navigate &bull; +/- Zoom &bull; F Fullscreen &bull; D Dark mode &bull; Esc Close
            </Typography>
          </Box>
        )}
      </Popover>

      {/* Tag Popover */}
      <Popover
        open={Boolean(tagAnchor)}
        anchorEl={tagAnchor}
        onClose={() => setTagAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, minWidth: 260, maxWidth: 320 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>Tags</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
            {attachmentTags.length === 0 && (
              <Typography variant="caption" color="text.secondary">No tags yet</Typography>
            )}
            {attachmentTags.map(tag => (
              <Chip key={tag} label={tag} size="small" onDelete={() => handleRemoveTag(tag)} sx={{ fontSize: '0.75rem' }} />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <TextField
              size="small"
              placeholder="Add a tag..."
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
              sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.75 } }}
            />
            <IconButton size="small" onClick={handleAddTag} disabled={!tagInput.trim()} color="primary">
              <CheckIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Popover>

      {/* Share Popover */}
      <Popover
        open={Boolean(shareAnchor)}
        anchorEl={shareAnchor}
        onClose={() => { setShareAnchor(null); setShareLink(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, minWidth: 300 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>Share Attachment</Typography>
          {generatingLink ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Generating link...</Typography>
            </Box>
          ) : shareLink ? (
            <>
              <TextField
                size="small"
                fullWidth
                value={shareLink}
                InputProps={{ readOnly: true, sx: { fontSize: '0.8rem' } }}
                sx={{ mb: 1.5 }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="contained" startIcon={<CopyIcon />} onClick={handleCopyShareLink} sx={{ textTransform: 'none', flex: 1 }}>
                  Copy Link
                </Button>
                <Button size="small" variant="outlined" startIcon={<ForwardIcon />} onClick={handleShareViaEmail} sx={{ textTransform: 'none', flex: 1 }}>
                  Email
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Link expires in 7 days
              </Typography>
            </>
          ) : (
            <Button size="small" variant="contained" onClick={handleGenerateShareLink} sx={{ textTransform: 'none' }}>
              Generate Share Link
            </Button>
          )}
        </Box>
      </Popover>

      {/* Content */}
      <DialogContent sx={{ p: 0, position: 'relative', overflow: 'hidden', bgcolor: isDarkMode ? '#0f3460' : undefined }}>
        {renderPreview()}
        
        {/* Navigation arrows */}
        {attachments.length > 1 && (
          <>
            <IconButton
              onClick={handlePrev}
              disabled={currentIndex === 0}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.9)',
                '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                boxShadow: 2,
              }}
            >
              <PrevIcon />
            </IconButton>
            <IconButton
              onClick={handleNext}
              disabled={currentIndex === attachments.length - 1}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.9)',
                '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                boxShadow: 2,
              }}
            >
              <NextIcon />
            </IconButton>
          </>
        )}
      </DialogContent>

      {/* Thumbnails for multiple attachments */}
      {attachments.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            p: 1,
            borderTop: 1,
            borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'divider',
            bgcolor: isDarkMode ? '#16213e' : '#f5f5f5',
            overflowX: 'auto',
          }}
        >
          {attachments.map((att, index) => (
            <Paper
              key={att.id}
              onClick={() => {
                setCurrentIndex(index);
                setZoom(1);
                setError(null);
              }}
              sx={{
                p: 1,
                cursor: 'pointer',
                minWidth: 80,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                border: index === currentIndex ? 2 : 1,
                borderColor: index === currentIndex ? 'primary.main' : (isDarkMode ? 'rgba(255,255,255,0.15)' : 'divider'),
                bgcolor: index === currentIndex ? (isDarkMode ? 'rgba(25,118,210,0.3)' : 'primary.light') : (isDarkMode ? 'rgba(255,255,255,0.05)' : 'white'),
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                },
              }}
            >
              {att.mimeType.startsWith('image/') && att.url ? (
                <Box
                  component="img"
                  src={att.url}
                  alt={att.filename}
                  sx={{
                    width: 48,
                    height: 48,
                    objectFit: 'cover',
                    borderRadius: 0.5,
                  }}
                />
              ) : (
                <Box sx={{ color: index === currentIndex ? 'primary.main' : 'text.secondary' }}>
                  {getFileIcon(att.mimeType)}
                </Box>
              )}
              <Typography
                variant="caption"
                sx={{
                  mt: 0.5,
                  maxWidth: 70,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                }}
              >
                {att.filename}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}

      {/* Snackbar notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

/* ═══ OnlyOffice Embedded Viewer Sub-component ═══ */
interface OnlyOfficeViewerProps {
  config: any;
  onlyofficeUrl: string;
  onReady?: () => void;
  onError?: () => void;
}

const OnlyOfficeViewer: React.FC<OnlyOfficeViewerProps> = ({ config, onlyofficeUrl, onReady, onError }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<any>(null);
  const editorId = useRef(`oo-viewer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

  useEffect(() => {
    const scriptSrc = `${onlyofficeUrl}/web-apps/apps/api/documents/api.js`;
    let script = document.querySelector(`script[src="${scriptSrc}"]`) as HTMLScriptElement | null;

    const initEditor = () => {
      if (editorRef.current) {
        try { editorRef.current.destroyEditor(); } catch { /* ignore */ }
        editorRef.current = null;
      }
      try {
        // @ts-ignore
        if (window.DocsAPI) {
          // @ts-ignore
          editorRef.current = new window.DocsAPI.DocEditor(editorId.current, {
            ...config,
            events: {
              onAppReady: () => onReady?.(),
              onDocumentReady: () => onReady?.(),
              onError: () => onError?.(),
              onWarning: () => { /* ignore */ },
            },
          });
        } else {
          onError?.();
        }
      } catch (err) {
        console.error('OnlyOffice init error:', err);
        onError?.();
      }
    };

    if (!script) {
      script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      script.onload = initEditor;
      script.onerror = () => onError?.();
      document.head.appendChild(script);
    } else {
      // @ts-ignore
      if (window.DocsAPI) {
        initEditor();
      } else {
        script.addEventListener('load', initEditor, { once: true });
      }
    }

    return () => {
      if (editorRef.current) {
        try { editorRef.current.destroyEditor(); } catch { /* ignore */ }
        editorRef.current = null;
      }
    };
  }, [config, onlyofficeUrl]);

  return (
    <div
      id={editorId.current}
      ref={containerRef}
      style={{ width: '100%', height: '100%' }}
    />
  );
};

export default AttachmentPreview;
